"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Image,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase.ts";
import { useAppTheme } from "../ThemeContext.tsx";

const SetNewPassword = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define colors based on theme
  const outerBg = isDark ? "gray.800" : "gray.100";
  const innerBg = isDark ? "gray.700" : "white";
  const headingColor = isDark ? "white" : "black";
  const textColor = isDark ? "gray.300" : "gray.600";
  const inputBg = isDark ? "gray.600" : "gray.200";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const dashboardIconUrl = isDark
    ? process.env.REACT_APP_DASHBOARD_DARK_ICON_URL || ""
    : process.env.REACT_APP_DASHBOARD_LIGHT_ICON_URL || "";

  useEffect(() => {
    const checkSession = async () => {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error("Session error:", error);
        setIsValidSession(false);
        return;
      }

      if (data.session) {
        console.log("Valid session found");
        setIsValidSession(true);
      } else {
        console.log("No valid session found");
        setIsValidSession(false);
      }
    };

    checkSession();
  }, []);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Password reset successful",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/login");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An error occurred during password reset",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isValidSession) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={outerBg}
      >
        <Box
          width="full"
          maxWidth="md"
          p={8}
          borderRadius="md"
          bg={innerBg}
          boxShadow="lg"
        >
          <VStack spacing={6}>
            {dashboardIconUrl && (
              <Image
                src={dashboardIconUrl || "/placeholder.svg"}
                alt="Logo"
                maxH="60px"
                mb={4}
              />
            )}
            <Heading color={headingColor}>Invalid Link</Heading>
            <Text color={textColor}>
              This password reset link is invalid or has expired.
            </Text>
            <Button
              onClick={() => navigate("/reset-password")}
              colorScheme="gray"
              _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
              background={isDark ? "gray.700" : "gray.200"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Request New Link
            </Button>
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={outerBg}
    >
      <Box
        width="full"
        maxWidth="md"
        p={8}
        borderRadius="md"
        bg={innerBg}
        boxShadow="lg"
      >
        <VStack spacing={6}>
          {dashboardIconUrl && (
            <Image
              src={dashboardIconUrl || "/placeholder.svg"}
              alt="Logo"
              maxH="60px"
              mb={4}
            />
          )}
          <Heading color={headingColor}>Set New Password</Heading>
          <Text color={textColor}>Please enter your new password below</Text>
          <form onSubmit={handlePasswordReset} style={{ width: "100%" }}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel color={textColor} id="new-password-label">
                  New Password
                </FormLabel>
                <InputGroup>
                  <Input
                    id="new-password-input"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    bg={inputBg}
                    color={headingColor}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      colorScheme={isDark ? "white" : "black"}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Confirm New Password</FormLabel>
                <InputGroup>
                  <Input
                    id="new-password-confirm-input"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    bg={inputBg}
                    color={headingColor}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                      icon={
                        showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                      }
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      variant="ghost"
                      colorScheme={isDark ? "white" : "black"}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <Button
                type="submit"
                colorScheme="gray"
                _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                background={isDark ? "gray.700" : "gray.200"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
                width="full"
                isLoading={loading}
              >
                Reset Password
              </Button>
            </VStack>
          </form>
        </VStack>
      </Box>
    </Box>
  );
};

export default SetNewPassword;
